import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Spacer,
  Switch,
  VStack,
} from '@chakra-ui/react';

const buildInitialState = (initialData = {}) => ({
  name: initialData.name || '',
  email: initialData.email || '',
  slug: initialData.slug || '',
  booking_url: initialData.booking_url || '',
  active: Boolean(initialData.active),
});

const ConsultantForm = ({
  title,
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = 'Save',
  apiErrors = {},
  showCard = true,
  hideCancel = false,
}) => {
  const [formValues, setFormValues] = useState(buildInitialState(initialData));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormValues(buildInitialState(initialData));
  }, [initialData]);

  useEffect(() => {
    setErrors(apiErrors || {});
  }, [apiErrors]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSwitchChange = (event) => {
    setFormValues((prev) => ({ ...prev, active: event.target.checked }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formValues.name.trim()) nextErrors.name = 'Name is required';
    if (!formValues.email.trim()) nextErrors.email = 'Email is required';
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSubmit({
      ...formValues,
      name: formValues.name.trim(),
      email: formValues.email.trim(),
      slug: formValues.slug.trim(),
      booking_url: formValues.booking_url.trim(),
    });
  };

  const content = (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired isInvalid={Boolean(errors.name)}>
        <FormLabel>Name</FormLabel>
        <Input
          name="name"
          value={formValues.name}
          onChange={handleInputChange}
          placeholder="Enter consultant name"
        />
        <FormErrorMessage>{errors.name}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={Boolean(errors.email)}>
        <FormLabel>Email</FormLabel>
        <Input
          type="email"
          name="email"
          value={formValues.email}
          onChange={handleInputChange}
          placeholder="consultant@example.com"
        />
        <FormErrorMessage>{errors.email}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={Boolean(errors.slug)}>
        <FormLabel>Slug</FormLabel>
        <Input
          name="slug"
          value={formValues.slug}
          onChange={handleInputChange}
          placeholder="consultant-slug"
        />
        <FormErrorMessage>{errors.slug}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={Boolean(errors.booking_url)}>
        <FormLabel>Booking URL</FormLabel>
        <Input
          name="booking_url"
          value={formValues.booking_url}
          onChange={handleInputChange}
          placeholder="https://..."
        />
        <FormErrorMessage>{errors.booking_url}</FormErrorMessage>
      </FormControl>

      <FormControl display="flex" alignItems="center" isInvalid={Boolean(errors.active)}>
        <FormLabel htmlFor="consultant-active" mb="0">
          Active
        </FormLabel>
        <Switch
          id="consultant-active"
          name="active"
          isChecked={formValues.active}
          onChange={handleSwitchChange}
          colorScheme="green"
        />
        <FormErrorMessage>{errors.active}</FormErrorMessage>
      </FormControl>

      {errors.base && (
        <Box color="red.500" fontSize="sm">
          {errors.base}
        </Box>
      )}

      <Flex>
        {!hideCancel ? (
          <>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Spacer />
          </>
        ) : null}
        <Button type="submit" colorScheme="green" variant="outline" isLoading={isSubmitting} ml={hideCancel ? 'auto' : 0}>
          {submitLabel}
        </Button>
      </Flex>
    </VStack>
  );

  return (
    <Box as="form" onSubmit={handleSubmit}>
      {showCard ? (
        <Card shadow="md" borderRadius="lg">
          <CardHeader pb={0}>
            <FormLabel fontSize="xl" fontWeight="bold" mb={0}>
              {title}
            </FormLabel>
          </CardHeader>
          <CardBody>{content}</CardBody>
        </Card>
      ) : (
        content
      )}
    </Box>
  );
};

export default ConsultantForm;
